// Imports for external libraries go here.
import React, { FC } from 'react';

import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MerchandisingSerpBannerNonEditable as MerchandisingSerpBanner } from '@marriott/mi-merch-components';
import { getImageWithDomain, getMediaData, getMediaDataSecondary, getMediaSource } from '../../utils/OfferUtils';
import { ImageRenditionsData } from '../../utils/ImageRenditionStructure';
import { getPropValue } from '../../utils/CommonUtils';
import { PRIMARY_MEDIA, SECONDARY_MEDIA } from '../../constants/CommonConstants';
import { OffersMerchandisingSerpBannerProps } from './OffersMerchandisingSerpBanner.types';
import { StyledOffersMerchandisingSerpBanner } from './OffersMerchandisingSerpBanner.styles';

export const OffersMerchandisingSerpBannerWrapperConfig = {
  emptyLabel: 'OffersMerchandisingSerpBannerWrapper',
  isEmpty: false,
  resourceType: `mi-aem-common-spa/components/content/serpbanner`,
};

export const OffersMerchandisingSerpBannerWrapper: FC<OffersMerchandisingSerpBannerProps> = props => {
  const { model, offersData, isAuthorMode } = props;
  const { envVarsObject } = useClientEnvVarsStore();
  const { offersSerpBanner } = ImageRenditionsData;

  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'];

  const merchandisingNode = offersData?.responseObject?.edges[0]?.merchandisingNode;
  const node = offersData?.responseObject?.edges[0]?.node;

  const mediaSource = isAuthorMode
    ? 'model'
    : getMediaSource(
        offersData?.responseObject?.edges[0]?.node?.media,
        PRIMARY_MEDIA,
        offersSerpBanner?.aspectRatio?.backgroundMedia
      );
  const isMediaFromDAC = mediaSource === PRIMARY_MEDIA;
  const media = isAuthorMode
    ? { rendition: [], fileReferenceBackgroundImage: '' }
    : getMediaData(
        offersData?.responseObject?.edges[0]?.node?.media,
        offersSerpBanner?.backgroundMedia,
        mediaSource,
        PRIMARY_MEDIA,
        imageDomain
      );
  const logoMediaSource = isAuthorMode
    ? 'model'
    : getMediaSource(offersData?.responseObject?.edges[0]?.node?.media, SECONDARY_MEDIA);

  const isLogoMediaFromDAC = logoMediaSource === SECONDARY_MEDIA;

  const logoMedia = isAuthorMode
    ? { rendition: [], fileReferenceBackgroundImage: '', assetPath: '' }
    : getMediaDataSecondary(
        offersData?.responseObject?.edges[0]?.node?.media,
        model?.image,
        imageDomain,
        logoMediaSource
      );

  const logoMediaUpdated = {
    ...logoMedia,
    imageSrc: logoMedia?.assetPath,
  };

  const fallbackLogoMedia = {
    altText: model?.logo?.altText,
    imageSrc: getImageWithDomain(model?.logo?.imageSrc, imageDomain),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requiredProps: any = {
    bullet1: getPropValue(isAuthorMode, node?.needToKnow[0], model?.bullet1),
    bullet2: getPropValue(isAuthorMode, node?.needToKnow[1], model?.bullet2),
    bullet3: getPropValue(isAuthorMode, node?.needToKnow[2], model?.bullet3),
    callToActionText1: getPropValue(isAuthorMode, node?.callToActionLabel, model?.callToActionText1),
    descriptionLong: getPropValue(isAuthorMode, node?.description, model?.descriptionLong),
    destinationUrl1: getPropValue(isAuthorMode, node?.callToActionLink, model?.destinationUrl1),
    displayEvergreenContent: model?.displayEvergreenContent ?? 'true',
    eyebrowText: getPropValue(isAuthorMode, node?.descriptionTeaser, model?.eyebrowText),
    headerMedium: getPropValue(isAuthorMode, node?.title, model?.headerMedium),
    image: isMediaFromDAC ? media : model?.image,
    imgAltText: model?.imgAltText,
    openInNewTab: model?.openInaNewTab,
    openInNewWindowAriaLabel: model?.openInNewWindowAriaLabel,
    brand: 'inverse',
    trackingProperties: model?.trackingProperties,
    logo: isLogoMediaFromDAC ? logoMediaUpdated : fallbackLogoMedia,
    metaData: merchandisingNode?.metaData,
    styleclass: 'offers-merchandising-serp-banner',
    isOffersMerchSerpBanner: true,
  };

  return (
    <StyledOffersMerchandisingSerpBanner
      data-testid="offers-OffersMerchandisingSerpBanner"
      data-component-name="o-offers-OffersMerchandisingSerpBanner"
    >
      <MerchandisingSerpBanner {...requiredProps} />
    </StyledOffersMerchandisingSerpBanner>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OffersMerchandisingSerpBanner = (props: any) => (
  <div data-component-name="o-offers-OffersMerchandisingSerpBanner" data-testid="offers-OffersMerchandisingSerpBanner">
    <EditableComponent config={OffersMerchandisingSerpBannerWrapperConfig} {...props}>
      <OffersMerchandisingSerpBannerWrapper {...props} />
    </EditableComponent>
  </div>
);
